import React, { useEffect, useState, useRef } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, TablePagination, Snackbar, Alert,
  Select, MenuItem, InputLabel, FormControl, Chip, Typography
} from "@mui/material";
import { Add, Edit, Delete, Undo } from "@mui/icons-material";
import { axiosInstances } from "./apiConfig";

const SERVICE_OPTIONS = ["Create", "Read", "Update", "Delete", "Export", "Upload"];
const PAGE_OPTIONS = ['Product', 'Admin', 'User', 'DataUpload', 'Price', 'Report',
      'Customer', 'Inventory', 'Payment', 'Invoice', 'Enquiry', 'Offer', 'Order'];

export default function RoleUI() {

  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const deleteTimers = useRef({});
  const [pendingDeletes, setPendingDeletes] = useState({});

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [total, setTotal] = useState(0);
  const [file, setFile] = useState(null);

  const loadRoles = async (pageNum = page, size = rowsPerPage) => {
    try {
      const res = await axiosInstances.userApi.get(`/roles?page=${pageNum}&size=${size}`);
      const payload = res.data.data || {};
      setRoles(payload.data || []);
      setTotal(payload.total || 0);
    } catch (err) {
      console.error("Error fetching roles", err);
    }
  };

  useEffect(() => {
    loadRoles();
  }, [page, rowsPerPage]);

  const handleOpen = (role = {}) => {
    // Convert comma-separated strings to arrays for multi-select
    setForm({
      ...role,
      authorisedServices: role.authorisedServices?.split(",") || [],
      authorisedPages: role.authorisedPages?.split(",") || []
    });
    setEditing(!!role.id);
    setFile(null);
    setOpen(true);
  };

  const handleClose = () => {
    setForm({});
    setOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSave = async () => {
  try {
    const token = localStorage.getItem("adminToken");

    if (file) {
      // ✅ Validate file type
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
        "application/vnd.ms-excel", // .xls
        "text/csv" // .csv
      ];

      if (!allowedTypes.includes(file.type)) {
        setSnackbar({
          open: true,
          message: "Only Excel or CSV files are allowed for role upload!",
          severity: "error",
        });
        return;
      }

      // ✅ Prepare and upload the file
      const excelFormData = new FormData();
      excelFormData.append("file", file);

      await axiosInstances.userApi.post(`/uploadRoles`, excelFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSnackbar({
        open: true,
        message: "Role Excel uploaded successfully!",
        severity: "success",
      });

    } else {
      // ✅ Manual role entry
      const rolePayload = {
        ...form,
        authorisedServices: (form.authorisedServices || []).join(','),
        authorisedPages: (form.authorisedPages || []).join(',')
      };

      const url = editing ? `/role/update` : `/roles`;

      await axiosInstances.userApi.post(url, rolePayload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      setSnackbar({
        open: true,
        message: editing ? "Role updated successfully!" : "Role created successfully!",
        severity: "success",
      });
    }

    loadRoles();     // 🔁 Refresh role list
    handleClose();   // ❌ Close modal
    setFile(null);   // 🧹 Clear uploaded file

  } catch (err) {
    if (err.response?.status === 409) {
      setSnackbar({ open: true, message: "Role name already exists", severity: "error" });
    } else if (err.response?.status === 401) {
      setSnackbar({ open: true, message: "Unauthorized - please log in as admin", severity: "error" });
    } else {
      setSnackbar({ open: true, message: "Something went wrong while saving the role!", severity: "error" });
    }
  }
};


  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };


  const handleDeleteClick = (id) => {
    setPendingDeletes((prev) => ({ ...prev, [id]: true }));
    deleteTimers.current[id] = setTimeout(async () => {
      try {
        await axiosInstances.userApi.post(`/role/delete`, { id });
        loadRoles();
      } catch (err) {
        console.error("Delete failed", err);
      } finally {
        setPendingDeletes((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        deleteTimers.current[id] = null;
      }
    }, 30000);
  };

  const handleUndoClick = (id) => {
    if (deleteTimers.current[id]) {
      clearTimeout(deleteTimers.current[id]);
      deleteTimers.current[id] = null;
    }
    setPendingDeletes((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Role Management</Typography>
      <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => handleOpen()}>
        Add Role
      </Button>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Authorised Services</TableCell>
              <TableCell>Authorised Pages</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role, index) => (
              <TableRow key={role.id}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{role.id}</TableCell>
                <TableCell>{role.role}</TableCell>
                <TableCell>{role.authorisedServices}</TableCell>
                <TableCell>{role.authorisedPages}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleOpen(role)}>
                    <Edit />
                  </IconButton>
                  {pendingDeletes[role.id] ? (
                    <IconButton color="warning" onClick={() => handleUndoClick(role.id)}>
                      <Undo />
                    </IconButton>
                  ) : (
                    <IconButton color="error" onClick={() => handleDeleteClick(role.id)}>
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {roles.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">No roles found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelDisplayedRows={({ page }) => `Page ${page + 1} of ${Math.ceil(total / rowsPerPage)}`}
        />
      </TableContainer>

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editing ? "Edit Role" : "Add Role"}</DialogTitle>
        <DialogContent>
          {editing && (
            <TextField fullWidth margin="dense" label="Role ID" value={form.id || ""} disabled />
          )}
          <TextField fullWidth margin="dense" label="Role Name" name="role" value={form.role || ""} onChange={handleChange} />

          <FormControl fullWidth margin="dense">
            <InputLabel>Authorised Services</InputLabel>
            <Select
              multiple
              name="authorisedServices"
              value={form.authorisedServices || []}
              onChange={handleChange}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => <Chip key={val} label={val} />)}
                </Box>
              )}
            >
              {SERVICE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel>Authorised Pages</InputLabel>
            <Select
              multiple
              name="authorisedPages"
              value={form.authorisedPages || []}
              onChange={handleChange}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => <Chip key={val} label={val} />)}
                </Box>
              )}
            >
              {PAGE_OPTIONS.map((page) => (
                <MenuItem key={page} value={page}>{page}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {!editing && (
            <>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ mt: 2 }}
              >
                Upload File
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {file.name}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editing ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
